/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { ICartItem } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import {
    AnalyticsContext,
    BottomBarContext,
    COMMON_LOGIN_STATUS_ERROR_CODE,
    loginHelper,
    PinGenerationStatus,
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    TableContext,
    useDigitalMenuTheme,
} from "@swiggy-private/portal-dineout-context";
import { useApiContextFetch } from "@swiggy-private/portal-dineout-context";
import {
    ErrorComponent,
    MultiVariantRemovePrompt,
    Overlay,
    PinVerificationCard,
} from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { TabType } from "types";
import { closeWebview, getBridgeSdk, isApp, manageParentOrderIdStorage } from "utils";
import { getScreenName } from "utils/analytics";
import { BackIcon, ErrorContainer } from "./styled";
import { verifyPinApi, IGeneratePinResponse, generatePin, IGeneratePinErrorInfo } from "../../api";
import { ROUTES, TABLE_ERROR_STATE } from "../../constants";
import { useBillPayment, IBillPaymentResponse } from "../../hooks/useBillPayment";
import { usePartnerOrderToast } from "../../hooks/useBillSync";
import { useCartActions } from "../../hooks/useCartActions";
import { isUserLoggedIn } from "../../utils/auth";
import { handleOrderToast, updateMenuItemInDish, waitForFABWidget } from "../../utils/menu";
import { onClickHandler } from "../../utils/menuClickHandler";
import { IPartnerOrderToast } from "../../utils/types";
import BillPaymentHalfCardRenderer from "../BillPaymentHalfCard/BillPaymentHalfCardRenderer";

export interface PinAuthProps {
    setHasBottomBar: React.Dispatch<React.SetStateAction<boolean>>;
    genericClickHandler: (_card?: ICartItem, extraParams?: Record<string, any>) => void;
    isCurtainRaiserShown: boolean;
    shouldAutoGeneratePin?: boolean;
}

export interface TableError {
    isError: boolean;
    errorInfo?: IGeneratePinErrorInfo;
    showBackButton: boolean;
}

export interface PinAuthHOCProps {
    setHasBottomBar: React.Dispatch<React.SetStateAction<boolean>>;
    isCurtainRaiserShown: boolean;
}

export interface WithPinAuthOptions {
    // Whether to automatically generate pin on component mount.
    shouldAutoGeneratePin?: boolean;
}

function withPinAuth<T extends PinAuthProps>(
    WrappedComponent: React.ComponentType<T>,
    { shouldAutoGeneratePin = true }: WithPinAuthOptions = {},
) {
    const PinAuthHOC = (props: Omit<T, keyof PinAuthProps> & PinAuthHOCProps) => {
        const { setHasBottomBar, isCurtainRaiserShown, ...restProps } = props;
        const { swiggyHeaders } = useContext(SwiggyContextHeader);
        const { tableId = "", restaurantId = "", parentOrderId = "", updateParentOrderId } = useContext(TableContext);
        const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
        const [pinVerifyError, setPinVerifyError] = useState("");
        const [pinStatusCode, setPinStatusCode] = useState<number | undefined>();
        const [tableError, setTableError] = useState<TableError>({ isError: false, showBackButton: true });
        const { state, dispatch } = useContext(ResponseContext);
        const makeApiCall = useApiContextFetch();
        const isLoggedIn = isUserLoggedIn(swiggyHeaders);
        const { setIsVisible } = useContext(BottomBarContext);
        const [loading, setLoading] = useState(true);
        const [showMultiVariantPrompt, setShowMultiVariantPrompt] = useState(false);
        const [showOverlay, setShowOverlay] = useState(false);
        const [billPaymentData, setBillPaymentData] = useState<IBillPaymentResponse | null>(null);
        const { onPartnerOrderDataReceived } = usePartnerOrderToast();
        const router = useRouter();
        const hasFetchedRef = useRef(false);
        const lastRouteRef = useRef<string | null>(null);
        const toastCleanupRef = useRef<(() => void) | undefined>(undefined);

        const screenName = getScreenName(router?.pathname || "");
        const { toggleTheme } = useDigitalMenuTheme();

        const clearToastTimeout = useCallback(() => {
            if (toastCleanupRef.current) {
                toastCleanupRef.current();
                toastCleanupRef.current = undefined;
            }
        }, []);

        const showMenuNudgeToast = useCallback(
            (partnerOrderToast: IPartnerOrderToast | undefined, showMenuNudge: boolean) => {
                if (!showMenuNudge || isEmpty(partnerOrderToast)) {
                    return;
                }
                // Clear any existing timeout/observer
                clearToastTimeout();
                // Wait for FAB widget to load and animation to complete before showing toast
                toastCleanupRef.current = waitForFABWidget(() => {
                    handleOrderToast({
                        partnerOrderToast,
                        showMenuNudge,
                        isCartItemAvailable: state.cart.localCart.cartItems.length > 0,
                        onBillSyncApiReceived: onPartnerOrderDataReceived,
                    });
                    toastCleanupRef.current = undefined;
                });
            },
            [clearToastTimeout, state.cart.localCart.cartItems.length, onPartnerOrderDataReceived],
        );

        const hideBottomBar = useCallback(() => {
            setIsVisible(false);
            if (setHasBottomBar) {
                setHasBottomBar(false);
            }
        }, [setHasBottomBar, setIsVisible]);

        const updateMenuPostCartUpdate = useCallback(
            (updatedCartItems: (typeof state.cart.localCart)["cartItems"]) => {
                const { filteredData, initialData } = state.menu;
                const { localCart } = state.cart;
                const cartData = { ...localCart, cartItems: updatedCartItems };

                // Keep initialData in sync so filter removals show correct quantities
                updateMenuItemInDish(initialData || [], cartData);
                const transformedMenuResponse = updateMenuItemInDish(filteredData || [], cartData);
                dispatch({
                    type: QrCodeActions.UPDATE_FILTER_DATA,
                    payload: { data: transformedMenuResponse, appliedFilters: state.menu.appliedFilters }, // to check that it doesn't break any flow, ask QA to include test cases accordingly
                });
            },
            [dispatch, state],
        );

        const { onAddButtonClick, verifyPinFlow, setVerifyPinFlow } = useCartActions(updateMenuPostCartUpdate);
        const { billPayment } = useBillPayment();

        const handlePinGenerationResponse = useCallback(
            (response: IGeneratePinResponse) => {
                if (!isEmpty(response?.parentOrderId)) {
                    manageParentOrderIdStorage(tableId, restaurantId, response?.parentOrderId);
                    updateParentOrderId?.(response?.parentOrderId);
                }
                switch (response?.status) {
                    case PinGenerationStatus.TABLE_CLOSED:
                        setTableError({
                            isError: true,
                            errorInfo: TABLE_ERROR_STATE,
                            showBackButton: true,
                        });
                        hideBottomBar();
                        break;

                    case PinGenerationStatus.FAILURE:
                        dispatch({
                            type: QrCodeActions.PIN_GENERATION_FAILURE,
                            payload: {},
                        });
                        break;

                    case PinGenerationStatus.BILL_GENERATED:
                        setTableError({
                            isError: true,
                            errorInfo: response?.errorInfo,
                            showBackButton: false,
                        });
                        hideBottomBar();
                        break;

                    default:
                        dispatch({
                            type: QrCodeActions.PIN_GENERATION_SUCCESS,
                            payload: { data: response },
                        });
                        if (response?.parentOrderId) {
                            manageParentOrderIdStorage(tableId, restaurantId, response?.parentOrderId);
                            updateParentOrderId?.(response?.parentOrderId);
                        }
                        if (response?.showMenuNudge) {
                            showMenuNudgeToast(response?.partnerOrderToast, Boolean(response?.showMenuNudge));
                        }
                }
            },
            [dispatch, tableId, restaurantId, updateParentOrderId, hideBottomBar, showMenuNudgeToast],
        );

        // Check if pin data was already prefetched by useMenuDataPrefetch
        const hasPrefetchedPinData = !isEmpty(state.pin.data?.status);
        const isPinLoading = state.pin.loading;

        const generatePinAndHandleResponse = useCallback(async () => {
            if (
                !isLoggedIn ||
                hasFetchedRef.current ||
                !tableId ||
                !restaurantId ||
                !makeApiCall ||
                !isCurtainRaiserShown
            ) {
                setLoading(false);
                return;
            }

            hasFetchedRef.current = true;

            try {
                const response = await generatePin({ tableId, restaurantId, parentOrderId }, makeApiCall);

                if (response?.data) {
                    setPinStatusCode(response?.statusCode);
                    handlePinGenerationResponse(response.data);
                } else {
                    dispatch({
                        type: QrCodeActions.PIN_GENERATION_FAILURE,
                        payload: {},
                    });
                }
            } catch (_) {
                dispatch({
                    type: QrCodeActions.PIN_GENERATION_FAILURE,
                    payload: {},
                });
            } finally {
                setLoading(false);
            }
        }, [
            isLoggedIn,
            tableId,
            restaurantId,
            parentOrderId,
            isCurtainRaiserShown,
            makeApiCall,
            dispatch,
            handlePinGenerationResponse,
        ]);

        const addToCart = useCallback(
            (newItem: any, isCustomisable?: boolean, isCrossSellItem?: boolean) => {
                if (!isEmpty(pinStatusCode) && COMMON_LOGIN_STATUS_ERROR_CODE.includes(pinStatusCode)) {
                    loginHelper({
                        sdk: getBridgeSdk(),
                        forwardPath: window.location.pathname + window.location.search,
                    });
                    return;
                }

                onAddButtonClick(newItem, isCustomisable, isCrossSellItem);
            },
            [onAddButtonClick, pinStatusCode],
        );

        const verifyPin = useCallback(
            async (pin: string) => {
                if (makeApiCall && swiggyHeaders && tableId && restaurantId) {
                    const response = await verifyPinApi({ tableId, restaurantId, pin: pin }, makeApiCall);
                    if (response?.data?.status === "SUCCESS") {
                        setVerifyPinFlow?.(false);
                        dispatch({
                            type: QrCodeActions.PIN_GENERATION_SUCCESS,
                            payload: {
                                data: {
                                    ...state.pin.data,
                                    pin,
                                    userStatus: PinGenerationStatus.USER_VERIFIED,
                                    parentOrderId: response?.data?.parentOrderId,
                                },
                            },
                        });
                        if (response?.data?.parentOrderId) {
                            manageParentOrderIdStorage(tableId, restaurantId, response?.data?.parentOrderId);
                            updateParentOrderId?.(response?.data?.parentOrderId);
                        }
                        if (response?.data?.showMenuNudge) {
                            showMenuNudgeToast(
                                response?.data?.partnerOrderToast,
                                Boolean(response?.data?.showMenuNudge),
                            );
                        }
                    } else {
                        setPinVerifyError("Wrong pin, Please try again");
                    }
                }
            },
            [
                dispatch,
                makeApiCall,
                restaurantId,
                setVerifyPinFlow,
                state.pin.data,
                swiggyHeaders,
                tableId,
                updateParentOrderId,
                showMenuNudgeToast,
            ],
        );

        const handleBack = useCallback(() => {
            if (isApp()) {
                closeWebview();
                return;
            }
            window.location.href = "https://swiggy.com/dineout";
            return;
        }, []);

        const handleShowMultiVariantPrompt = useCallback(() => {
            setShowMultiVariantPrompt(true);
        }, []);

        const handleMultiVariantPromptCancel = useCallback(() => {
            setShowMultiVariantPrompt(false);
        }, []);

        const handleMultiVariantPromptViewCart = useCallback(() => {
            setShowMultiVariantPrompt(false);
            router?.push(`${ROUTES.CART_ORDERS}?tab=${TabType.CART}`);
        }, [router]);

        const handleBillPayment = useCallback(async (): Promise<boolean> => {
            setShowOverlay(true);
            try {
                const result = await billPayment();
                setShowOverlay(false);
                if (result) {
                    setBillPaymentData(result);
                    return true;
                }
                return false;
            } catch (_error) {
                setShowOverlay(false);
                return false;
            }
        }, [billPayment]);

        const handleBillPaymentClose = useCallback(() => {
            setBillPaymentData(null);
        }, []);

        const genericClickHandler = useCallback(
            (_card?: ICartItem, extraParams: Record<string, any> = {}) => {
                onClickHandler({
                    state,
                    onAddButtonClick: addToCart,
                    extraParams,
                    dispatch,
                    router,
                    toggleTheme,
                    onShowMultiVariantPrompt: handleShowMultiVariantPrompt,
                    onBillPayment: handleBillPayment,
                });
            },
            [state, addToCart, dispatch, router, toggleTheme, handleShowMultiVariantPrompt, handleBillPayment],
        );

        const handleErrorCtaClick = useCallback(() => {
            genericClickHandler({} as ICartItem, { cta: tableError?.errorInfo?.cta });
        }, [genericClickHandler, tableError?.errorInfo?.cta]);

        // Reset ref on route change to ensure PIN is called on every page
        useEffect(() => {
            hasFetchedRef.current = false;
            lastRouteRef.current = router?.pathname || null;
        }, [router?.pathname]);

        // Cleanup timeout on unmount
        useEffect(() => {
            return () => {
                clearToastTimeout();
            };
        }, [clearToastTimeout]);

        // Handle PIN generation logic
        useEffect(() => {
            // Skip pin generation if disabled
            if (!shouldAutoGeneratePin) {
                return;
            }

            // Reset ref when PIN was reset
            if (isPinLoading && !hasPrefetchedPinData) {
                hasFetchedRef.current = false;
            }

            // Wait if PIN is being fetched and we already have data
            if (isPinLoading && hasPrefetchedPinData) {
                return;
            }

            // Always generate PIN if conditions are met (don't use prefetched data)
            if (
                isLoggedIn &&
                tableId &&
                restaurantId &&
                makeApiCall &&
                !hasFetchedRef.current &&
                isCurtainRaiserShown
            ) {
                generatePinAndHandleResponse();
            } else if (!isLoggedIn) {
                setLoading(false);
            }
        }, [
            isLoggedIn,
            tableId,
            restaurantId,
            makeApiCall,
            generatePinAndHandleResponse,
            hasPrefetchedPinData,
            isPinLoading,
            isCurtainRaiserShown,
            router?.pathname,
        ]);

        if (tableError?.isError) {
            return (
                <ErrorContainer>
                    {tableError?.showBackButton && (
                        <BackIcon onClick={handleBack}>
                            <SpriteIconWeb iconName="arrowBack20" fillColor="text_Highest_Emphasis" />
                        </BackIcon>
                    )}

                    <ErrorComponent
                        heading={tableError?.errorInfo?.title}
                        description={tableError?.errorInfo?.description}
                        showCta={!isEmpty(tableError?.errorInfo?.cta?.text)}
                        imgSrc={getCloudinaryUrl(tableError?.errorInfo?.imageId || "")}
                        ctaBtnType={tableError?.errorInfo?.cta?.dlsType}
                        showFullPage={true}
                        onCtaClick={handleErrorCtaClick}
                        ctaText={tableError?.errorInfo?.cta?.text}
                    />
                </ErrorContainer>
            );
        }

        if (isEmpty(tableId) || isEmpty(restaurantId)) {
            return null;
        }

        return (
            <>
                <WrappedComponent
                    {...(restProps as T)}
                    setHasBottomBar={setHasBottomBar}
                    genericClickHandler={genericClickHandler}
                    isCurtainRaiserShown={isCurtainRaiserShown}
                    isPinLoading={loading}
                />

                {verifyPinFlow && (
                    <PinVerificationCard
                        showHalfCard={verifyPinFlow}
                        userName={state.pin?.data?.hostName}
                        closeHalfCard={() => {
                            setVerifyPinFlow?.(false);
                            setPinVerifyError("");
                        }}
                        onCtaClick={verifyPin}
                        pinErrorText={pinVerifyError}
                        screenName={screenName}
                        impressionAnalytics={impressionEvent}
                        clickAnalytics={clickEvent}
                    />
                )}

                <MultiVariantRemovePrompt
                    isOpen={showMultiVariantPrompt}
                    onCancel={handleMultiVariantPromptCancel}
                    onViewCart={handleMultiVariantPromptViewCart}
                />

                {showOverlay && <Overlay />}

                {/* TODO: Merge this with bottombar BillPaymentHalfCardRenderer. Will pick in next release */}
                {billPaymentData && (
                    <BillPaymentHalfCardRenderer billPaymentData={billPaymentData} onClose={handleBillPaymentClose} />
                )}
            </>
        );
    };

    PinAuthHOC.displayName = `withPinAuth(${WrappedComponent.displayName || WrappedComponent.name})`;

    return memo(PinAuthHOC);
}

export default withPinAuth;
